import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import umbrella from 'umbrella-storage';
import Cookies from 'js-cookie';
import CustomerManagerService, { ICustomerManagerInfo, ICustomerManagerInfoList } from '../../service/customerManager/CustomerManagerService';
import { CooperationStates, getCooperationStates } from '../../components/public/CooperationStatesSelect';
import './page-customer-manager.less';

interface CustomerManagerTableState {
    pageData: ICustomerManagerInfoList | null;
    sourceData: ICustomerManagerInfo[];
    nameInputValue: string;
    managerTypeValue: number | null;
    statesSelectValue: number | null;
    pagination: any;
}

const defaultPageSize = 5;
const getPageSize = () => umbrella.getLocalStorage('customerManagerPageSize') || defaultPageSize;
const splitRole = (role: number) => {
    const parts = [1000, 100, 10, 2, 1];
    const result = new Set<number>();
    let remain = role;
    parts.forEach((part) => {
        if (remain >= part) {
            remain -= part;
            result.add(part);
        }
    });
    if (remain !== 0) {
        result.clear();
        result.add(role);
    }
    return result;
};
const canEditCustomerManager = () => {
    const role = parseInt(Cookies.get('userRole') || '0', 10);
    const roleParts = splitRole(role);
    return roleParts.has(1) || roleParts.has(100) || roleParts.has(1000);
};

class CustomerManagerTable extends Component<any, CustomerManagerTableState> {
    state: CustomerManagerTableState = {
        pageData: { records: [], total: 0, size: 0, current: 1, pages: 0 },
        sourceData: [],
        nameInputValue: '',
        managerTypeValue: null,
        statesSelectValue: CooperationStates.normal,
        pagination: {
            current: 1,
            pageSize: getPageSize(),
            total: 0,
        },
    };

    getOperationColumnWidth = (buttonCount: number) => Math.max(78, buttonCount * 68 + 14);

    getOperationButtonCount = () => {
        if (!canEditCustomerManager()) {
            return 0;
        }
        return 1; // 当前仅“编辑”
    };

    getColumns = (): ColumnProps<any>[] => {
        const operationWidth = this.getOperationColumnWidth(this.getOperationButtonCount());

        return [
            {
                title: '姓名',
                width: 80,
                dataIndex: 'name',
                key: 'name',
                render: (text: string, record: ICustomerManagerInfo) => (<a href={`/#/app/customerManager/view?id=${record.id}`}>{text}</a>),
            },
            { title: '类型', width: 60, dataIndex: 'managerTypeText', key: 'managerTypeText' },
            { title: '居住城市', width: 80, dataIndex: 'residenceCity', key: 'residenceCity' },
            { title: '提成方式', width: 120, dataIndex: 'commissionMode', key: 'commissionMode' },
            { title: '支付方式', width: 110, dataIndex: 'paymentMode', key: 'paymentMode' },
            { title: '身份证', width: 110, dataIndex: 'idCardNo', key: 'idCardNo' },
            { title: '状态', width: 60, dataIndex: 'statesText', key: 'statesText' },
            {
                title: '操作',
                key: 'operation',
                fixed: 'right',
                width: operationWidth,
                onHeaderCell: () => ({ style: { width: operationWidth, minWidth: operationWidth, maxWidth: operationWidth } }),
                onCell: () => ({ style: { width: operationWidth, minWidth: operationWidth, maxWidth: operationWidth, whiteSpace: 'nowrap' } }),
                render: (record: ICustomerManagerInfo) => (
                    <>
                        {canEditCustomerManager() && <button className="table-action-btn" onClick={() => this.handleEditButtonClick(record)}>编辑</button>}
                    </>
                ),
            },
        ];
    };

    setPaginationPage = (pageNo: number) => {
        this.setState({ pagination: { ...this.state.pagination, current: pageNo } });
    };

    getCustomerManagerTableData = async (pageNo: number, name?: string, managerType?: number | null, states?: number | null) => {
        const nextName = name !== undefined ? name : this.state.nameInputValue;
        const nextManagerType = managerType !== undefined ? managerType : this.state.managerTypeValue;
        const nextStates = states !== undefined ? states : this.state.statesSelectValue;

        const response = await CustomerManagerService.getCustomerManagerList({
            queryParam: {
                name: nextName,
                managerType: nextManagerType ?? undefined,
                states: nextStates ?? undefined,
            },
            pageNo,
            pageSize: this.state.pagination.pageSize,
        });

        if (response && response.data && response.data.customerManagerInfoList) {
            const pageData: ICustomerManagerInfoList = response.data.customerManagerInfoList;
            const records = pageData.records || [];
            const sourceData = records.map((item: any) => ({
                ...item,
                managerTypeText: item.managerType === 1 ? '内部销售' : '外部渠道',
                statesText: getCooperationStates(item.states),
            }));

            this.setState({
                pageData: { ...pageData, current: pageNo },
                sourceData,
                pagination: { ...this.state.pagination, total: pageData.total, current: pageNo },
                nameInputValue: nextName,
                managerTypeValue: nextManagerType,
                statesSelectValue: nextStates,
            });
        }
    };

    handleChangePage = (pageNo: number) => {
        this.getCustomerManagerTableData(pageNo);
    };

    changePageSize = (current: number, pageSize: number) => {
        umbrella.setLocalStorage('customerManagerPageSize', pageSize);
        this.setState({ pagination: { ...this.state.pagination, current: 1, pageSize } }, () => {
            this.getCustomerManagerTableData(1);
        });
    };

    handleEditButtonClick = (record: ICustomerManagerInfo) => {
        window.location.href = `/#/app/customerManager/manager?id=${record.id}`;
    };

    render() {
        const dataWithKeys = (this.state.sourceData || []).map((row) => ({ ...row, key: row.id }));

        return (
            <Table
                size="small"
                columns={this.getColumns()}
                dataSource={dataWithKeys}
                tableLayout="fixed"
                pagination={{
                    showSizeChanger: true,
                    current: this.state.pagination.current,
                    pageSize: this.state.pagination.pageSize,
                    total: this.state.pagination.total,
                    pageSizeOptions: ['5', '10', '20', '50', '100'],
                    showTotal: (total, range) => `显示第 ${range[0]} 到第 ${range[1]} 条记录，共 ${total} 条记录`,
                    onShowSizeChange: this.changePageSize,
                    onChange: this.handleChangePage,
                }}
                scroll={{ x: 1120, y: 360 }}
            />
        );
    }
}

export default CustomerManagerTable;
